interface FieldHelpProps {
  /** The explanatory text shown in the tooltip. */
  text: string;
  /** Optional label of the control this helps, used for the accessible name. */
  label?: string;
}

/**
 * Small inline help affordance: a "?" icon that reveals an explanatory tooltip on hover or keyboard
 * focus. Pure CSS (group-hover / group-focus-within) so it needs no popover library, and it is
 * keyboard-accessible (the trigger is a real focusable button and the bubble carries role="tooltip").
 * Safe to place inside a &lt;label&gt; — the click is suppressed so it never toggles the associated control.
 */
export function FieldHelp({ text, label }: FieldHelpProps): JSX.Element {
  return (
    <span className="group relative inline-flex align-middle">
      <button
        type="button"
        aria-label={label !== undefined ? `Ayuda: ${label}` : 'Ayuda'}
        onClick={(event) => event.preventDefault()}
        className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-slate-300 text-[10px] font-semibold text-slate-400 transition-colors hover:border-primary hover:text-primary focus:outline-none focus:ring-1 focus:ring-primary"
      >
        ?
      </button>
      <span
        role="tooltip"
        className="pointer-events-none absolute left-1/2 top-full z-20 mt-1 w-56 -translate-x-1/2 rounded-md bg-slate-800 px-2.5 py-1.5 text-xs font-normal leading-snug text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100"
      >
        {text}
      </span>
    </span>
  );
}
